import React, { useState } from 'react';

const integrations = [
  { name: 'Salesforce', color: '#00A1E0', letter: 'SF' },
  { name: 'HubSpot', color: '#FF7A59', letter: 'HS' },
  { name: 'Pipedrive', color: '#28A745', letter: 'PD' },
  { name: 'Zapier', color: '#FF4A00', letter: 'ZP' },
  { name: 'Clay', color: '#7C3AED', letter: 'CL' },
  { name: 'Outreach', color: '#5B5BD6', letter: 'OR' },
  { name: 'Apollo', color: '#0066CC', letter: 'AP' },
  { name: 'Make', color: '#6D28D9', letter: 'MK' },
];

const enrichTabs = [
  { id: 0, label: 'Enrich emails from names and company names' },
  { id: 1, label: 'Find job titles from company domain' },
  { id: 2, label: 'Enrich firmographics from any company' },
];

const enrichExamples = [
  [
    { name: 'Sarah Johnson', company: 'Acme Corp', email: 's.johnson@acme.com', status: 'verified' },
    { name: 'Mark Stevens', company: 'TechStart', email: 'm.stevens@tech.io', status: 'verified' },
    { name: 'Priya Patel', company: 'ScaleUp', email: 'p.patel@scaleup.co', status: 'verified' },
    { name: 'James Wren', company: 'DevHouse', email: 'j.wren@devhouse.io', status: 'not found' },
  ],
  [
    { name: 'acme.com', domain: 'Enterprise', title: 'VP Sales', count: '14 contacts' },
    { name: 'techstart.io', domain: 'SMB', title: 'CEO', count: '3 contacts' },
    { name: 'scaleup.co', domain: 'Growth', title: 'Head of Growth', count: '8 contacts' },
    { name: 'devhouse.io', domain: 'Agency', title: 'CTO', count: '5 contacts' },
  ],
  [
    { name: 'Acme Corp', industry: 'Software', size: '501-1000', revenue: '$50M-100M', country: 'USA' },
    { name: 'TechStart', industry: 'SaaS', size: '11-50', revenue: '$1M-5M', country: 'UK' },
    { name: 'ScaleUp', industry: 'Marketing', size: '51-200', revenue: '$5M-20M', country: 'Canada' },
    { name: 'DevHouse', industry: 'Agency', size: '11-50', revenue: '$2M-8M', country: 'Germany' },
  ],
];

export default function Integrations() {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Integrations grid */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">Integrate into any workflow</h2>
          <p className="text-gray-500 text-lg">Connect Director Discovery to your existing tools in minutes</p>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-8">
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-4 mb-6">
            {integrations.map((tool) => (
              <div key={tool.name} className="flex flex-col items-center gap-2 group">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: tool.color }}
                >
                  {tool.letter}
                </div>
                <span className="text-xs text-gray-500 text-center">{tool.name}</span>
              </div>
            ))}
          </div>
          <div className="text-center">
            <a href="#" className="text-indigo-600 text-sm font-semibold hover:underline">Learn more about integrations →</a>
          </div>
        </div>

        {/* Enrich section */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Enrich any data from any source</h3>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Tabs */}
            <div className="lg:w-64 flex-shrink-0 space-y-2">
              {enrichTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <a href="#" className="block mt-4 text-indigo-600 text-sm font-semibold hover:underline px-4">
                Learn more →
              </a>
            </div>

            {/* Preview table */}
            <div className="flex-1 overflow-auto">
              <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
                {activeTab === 0 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200">
                        <th className="px-4 py-2.5 text-left font-semibold">Name</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Company</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Email</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichExamples[0].map((row :any, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-white transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                          <td className="px-4 py-3 text-gray-500">{row.company}</td>
                          <td className="px-4 py-3 text-indigo-600 font-mono text-xs">{row.email}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${row.status === 'verified' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {activeTab === 1 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200">
                        <th className="px-4 py-2.5 text-left font-semibold">Domain</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Type</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Top Title</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Contacts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichExamples[1].map((row :any, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-white transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                          <td className="px-4 py-3 text-gray-500">{row.domain}</td>
                          <td className="px-4 py-3 text-indigo-600">{row.title}</td>
                          <td className="px-4 py-3 text-gray-600 font-medium">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {activeTab === 2 && (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-200">
                        <th className="px-4 py-2.5 text-left font-semibold">Company</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Industry</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Size</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichExamples[2].map((row :any, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-white transition-colors">
                          <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                          <td className="px-4 py-3 text-gray-500">{row.industry}</td>
                          <td className="px-4 py-3 text-gray-600">{row.size}</td>
                          <td className="px-4 py-3 text-indigo-600 font-medium">{row.revenue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
